import type {
  AssessmentBlueprintCapability,
  AssessmentBlueprintDetail,
  AssessmentBlueprintQuestion,
  AssessmentBlueprintSubCapability,
  AssessmentBlueprintSummary,
} from '../types/assessmentBlueprint';
import { FRAMEWORK_STUDIO_LABELS } from './frameworkStudioLabels';
import { QUESTION_STATE } from './subCapabilityQuestionUtils';

const pick = <T>(...values: T[]): T | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeQuestion = (raw: Record<string, unknown>): AssessmentBlueprintQuestion => ({
  id: Number(pick(raw.id, raw.Id) ?? 0),
  blueprintSubCapabilityId: pick(raw.blueprintSubCapabilityId, raw.BlueprintSubCapabilityId) as number | null | undefined,
  blueprintCapabilityId: pick(raw.blueprintCapabilityId, raw.BlueprintCapabilityId) as number | null | undefined,
  sourceQuestionId: pick(raw.sourceQuestionId, raw.SourceQuestionId) as number | null | undefined,
  questionText: String(pick(raw.questionText, raw.QuestionText) ?? ''),
  stateKind:
    String(pick(raw.stateKind, raw.StateKind) ?? QUESTION_STATE.current) === QUESTION_STATE.future
      ? QUESTION_STATE.future
      : QUESTION_STATE.current,
  displayOrder: Number(pick(raw.displayOrder, raw.DisplayOrder) ?? 0),
  isIncluded: pick(raw.isIncluded, raw.IsIncluded) !== false,
  isActive: pick(raw.isActive, raw.IsActive) !== false,
});

const normalizeSubCapability = (raw: Record<string, unknown>): AssessmentBlueprintSubCapability => ({
  id: Number(pick(raw.id, raw.Id) ?? 0),
  sourceSubCapabilityId: Number(pick(raw.sourceSubCapabilityId, raw.SourceSubCapabilityId) ?? 0),
  subCapabilityName: String(pick(raw.subCapabilityName, raw.SubCapabilityName) ?? ''),
  subCapabilityDescription: String(pick(raw.subCapabilityDescription, raw.SubCapabilityDescription) ?? ''),
  currentStateCommentPlaceholder: pick(
    raw.currentStateCommentPlaceholder,
    raw.CurrentStateCommentPlaceholder
  ) as string | null | undefined,
  futureStateCommentPlaceholder: pick(
    raw.futureStateCommentPlaceholder,
    raw.FutureStateCommentPlaceholder
  ) as string | null | undefined,
  maturityOptionsJson: pick(raw.maturityOptionsJson, raw.MaturityOptionsJson) as string | null | undefined,
  displayOrder: Number(pick(raw.displayOrder, raw.DisplayOrder) ?? 0),
  isIncluded: pick(raw.isIncluded, raw.IsIncluded) !== false,
  currentStateQuestions: (Array.isArray(raw.currentStateQuestions) ? raw.currentStateQuestions : [])
    .map((item) => normalizeQuestion(item as Record<string, unknown>)),
  futureStateQuestions: (Array.isArray(raw.futureStateQuestions) ? raw.futureStateQuestions : [])
    .map((item) => normalizeQuestion(item as Record<string, unknown>)),
});

const normalizeCapability = (raw: Record<string, unknown>): AssessmentBlueprintCapability => ({
  id: Number(pick(raw.id, raw.Id) ?? 0),
  sourceCapabilityId: Number(pick(raw.sourceCapabilityId, raw.SourceCapabilityId) ?? 0),
  capabilityName: String(pick(raw.capabilityName, raw.CapabilityName) ?? ''),
  capabilityIconUrl: String(pick(raw.capabilityIconUrl, raw.CapabilityIconUrl) ?? ''),
  capabilityDescription: String(pick(raw.capabilityDescription, raw.CapabilityDescription) ?? ''),
  currentStateCommentPlaceholder: pick(
    raw.currentStateCommentPlaceholder,
    raw.CurrentStateCommentPlaceholder
  ) as string | null | undefined,
  futureStateCommentPlaceholder: pick(
    raw.futureStateCommentPlaceholder,
    raw.FutureStateCommentPlaceholder
  ) as string | null | undefined,
  maturityOptionsJson: pick(raw.maturityOptionsJson, raw.MaturityOptionsJson) as string | null | undefined,
  displayOrder: Number(pick(raw.displayOrder, raw.DisplayOrder) ?? 0),
  isIncluded: pick(raw.isIncluded, raw.IsIncluded) !== false,
  currentStateQuestions: (Array.isArray(raw.currentStateQuestions) ? raw.currentStateQuestions : [])
    .map((item) => normalizeQuestion(item as Record<string, unknown>)),
  futureStateQuestions: (Array.isArray(raw.futureStateQuestions) ? raw.futureStateQuestions : [])
    .map((item) => normalizeQuestion(item as Record<string, unknown>)),
  subCapabilities: (Array.isArray(raw.subCapabilities) ? raw.subCapabilities : [])
    .map((item) => normalizeSubCapability(item as Record<string, unknown>)),
});

export const normalizeAssessmentBlueprintSummary = (
  raw: Record<string, unknown>
): AssessmentBlueprintSummary => ({
  id: Number(pick(raw.id, raw.Id) ?? 0),
  title: String(pick(raw.title, raw.Title) ?? ''),
  description: pick(raw.description, raw.Description) as string | null | undefined,
  status: String(pick(raw.status, raw.Status) ?? 'Draft') as AssessmentBlueprintSummary['status'],
  sourceTraxccelFrameworkId: Number(pick(raw.sourceTraxccelFrameworkId, raw.SourceTraxccelFrameworkId) ?? 0),
  sourceTraxccelFrameworkTitle: String(
    pick(raw.sourceTraxccelFrameworkTitle, raw.SourceTraxccelFrameworkTitle) ?? ''
  ),
  companyId: pick(raw.companyId, raw.CompanyId) as number | null | undefined,
  companyName: pick(raw.companyName, raw.CompanyName) as string | null | undefined,
  projectId: pick(raw.projectId, raw.ProjectId) as number | null | undefined,
  projectName: pick(raw.projectName, raw.ProjectName) as string | null | undefined,
  publishedAt: pick(raw.publishedAt, raw.PublishedAt) as string | null | undefined,
  createdAt: String(pick(raw.createdAt, raw.CreatedAt) ?? ''),
  updatedAt: pick(raw.updatedAt, raw.UpdatedAt) as string | null | undefined,
  capabilityCount: Number(pick(raw.capabilityCount, raw.CapabilityCount) ?? 0),
  subCapabilityCount: Number(pick(raw.subCapabilityCount, raw.SubCapabilityCount) ?? 0),
});

export const normalizeAssessmentBlueprintDetail = (
  raw: Record<string, unknown> | null | undefined
): AssessmentBlueprintDetail => {
  const summary = normalizeAssessmentBlueprintSummary(raw ?? {});
  return {
    ...summary,
    publishedBy: pick(raw?.publishedBy, raw?.PublishedBy) as number | null | undefined,
    createdBy: pick(raw?.createdBy, raw?.CreatedBy) as number | null | undefined,
    questionCount: Number(pick(raw?.questionCount, raw?.QuestionCount) ?? 0),
    capabilities: (Array.isArray(raw?.capabilities) ? raw.capabilities : [])
      .map((item) => normalizeCapability(item as Record<string, unknown>)),
  };
};

export const buildSavePayload = (
  blueprint: AssessmentBlueprintDetail
): import('../types/assessmentBlueprint').SaveAssessmentBlueprintPayload => ({
  id: blueprint.id,
  title: blueprint.title,
  description: blueprint.description ?? null,
  companyId: blueprint.companyId ?? null,
  projectId: blueprint.projectId ?? null,
  capabilities: blueprint.capabilities.map((cap) => ({
    ...cap,
    isIncluded: true,
    currentStateQuestions: (cap.currentStateQuestions || [])
      .filter((q) => q.id > 0 || String(q.questionText || '').trim())
      .map((q, index) => ({
        ...q,
        displayOrder: index,
        isIncluded: true,
        isActive: q.isActive !== false,
      })),
    futureStateQuestions: (cap.futureStateQuestions || [])
      .filter((q) => q.id > 0 || String(q.questionText || '').trim())
      .map((q, index) => ({
        ...q,
        displayOrder: index,
        isIncluded: true,
        isActive: q.isActive !== false,
      })),
    subCapabilities: (cap.subCapabilities || []).map((sub) => ({
      ...sub,
      isIncluded: true,
      currentStateQuestions: (sub.currentStateQuestions || [])
        .filter((q) => q.id > 0 || String(q.questionText || '').trim())
        .map((q, index) => ({
          ...q,
          displayOrder: index,
          isIncluded: true,
          isActive: q.isActive !== false,
        })),
      futureStateQuestions: (sub.futureStateQuestions || [])
        .filter((q) => q.id > 0 || String(q.questionText || '').trim())
        .map((q, index) => ({
          ...q,
          displayOrder: index,
          isIncluded: true,
          isActive: q.isActive !== false,
        })),
    })),
  })),
});

export type BlueprintAssessmentUnit =
  | AssessmentBlueprintCapability
  | AssessmentBlueprintSubCapability;

export function blueprintUnitName(unit: BlueprintAssessmentUnit): string {
  return 'subCapabilityName' in unit ? unit.subCapabilityName : unit.capabilityName;
}

/** Which capability / sub-capability row is being edited in the blueprint wizard. */
export function resolveBlueprintEditTarget(
  capability: AssessmentBlueprintCapability | null | undefined,
  activeSubId: number | null
): { capabilityId: number; subId: number | null; unit: BlueprintAssessmentUnit } | null {
  if (!capability) return null;

  if (capability.subCapabilities.length === 0) {
    return { capabilityId: capability.id, subId: null, unit: capability };
  }

  const sub =
    capability.subCapabilities.find((row) => row.id === activeSubId) ??
    capability.subCapabilities[0];
  if (!sub) return null;

  return { capabilityId: capability.id, subId: sub.id, unit: sub };
}

export function updateBlueprintUnit(
  capabilities: AssessmentBlueprintCapability[],
  capabilityId: number,
  subId: number | null,
  patch: Partial<BlueprintAssessmentUnit>
): AssessmentBlueprintCapability[] {
  return capabilities.map((cap) => {
    if (cap.id !== capabilityId) return cap;
    if (subId == null) return { ...cap, ...patch };
    return {
      ...cap,
      subCapabilities: cap.subCapabilities.map((sub) =>
        sub.id === subId ? { ...sub, ...patch } : sub
      ),
    };
  });
}

export const countIncludedQuestions = (blueprint: AssessmentBlueprintDetail): number => {
  let count = 0;
  for (const cap of blueprint.capabilities) {
    if (cap.subCapabilities.length === 0) {
      count += [...cap.currentStateQuestions, ...cap.futureStateQuestions].filter(
        (q) => q.isActive && q.questionText.trim()
      ).length;
      continue;
    }
    for (const sub of cap.subCapabilities) {
      count += [...sub.currentStateQuestions, ...sub.futureStateQuestions].filter(
        (q) => q.isActive && q.questionText.trim()
      ).length;
    }
  }
  return count;
};

export const WIZARD_STEPS = [
  { key: 'framework', label: FRAMEWORK_STUDIO_LABELS.assessmentFrameworks },
  { key: 'questions', label: 'Questions' },
  { key: 'company', label: 'Company' },
  { key: 'project', label: 'Project' },
  { key: 'publish', label: 'Review & publish' },
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number]['key'];
